import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface CellContextMenuProps {
  /** Viewport X coordinate (pixels) where the menu should anchor. */
  x: number;
  /** Viewport Y coordinate (pixels) where the menu should anchor. */
  y: number;
  assigneeKind: 'person' | 'team';
  /** Display name for the clicked person row; empty/undefined for team rows. */
  assigneeName?: string;
  /** True when the clicked row belongs to the current user. */
  isSelfRow: boolean;
  /** True when the current user can request leave for the clicked user
   *  (self, or admin/approver clicking someone else). */
  canRequestLeave: boolean;
  /** True when the current user can log time for the clicked user. */
  canLogTime: boolean;
  onCreateItem: () => void;
  onRequestLeave: () => void;
  onLogTime: () => void;
  onClose: () => void;
}

// ─── Styles (inline; mirrors Jira plugin's .cell-context-menu look) ──────────

const MENU_STYLE: React.CSSProperties = {
  position: 'fixed',
  zIndex: 1000,
  minWidth: 200,
  padding: '6px 0',
  background: '#ffffff',
  borderRadius: 8,
  boxShadow: '0 8px 24px rgba(9, 30, 66, 0.2), 0 0 1px rgba(9, 30, 66, 0.3)',
  fontFamily: 'inherit',
};

const ITEM_BASE: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  width: '100%',
  padding: '9px 16px',
  background: 'none',
  border: 'none',
  fontSize: 13,
  fontWeight: 500,
  color: '#44546F',
  cursor: 'pointer',
  textAlign: 'left',
  transition: 'background 0.1s, color 0.1s',
  font: 'inherit',
};

const ITEM_HOVER: React.CSSProperties = {
  background: '#F1F2F4',
  color: '#0052CC',
};

const ITEM_DISABLED: React.CSSProperties = {
  color: '#B3B9C4',
  cursor: 'not-allowed',
};

const ICON_SLOT: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 20,
  height: 20,
  color: '#626F86',
  flexShrink: 0,
};

const ICON_SLOT_HOVER: React.CSSProperties = {
  color: '#0052CC',
};

const ICON_SLOT_DISABLED: React.CSSProperties = {
  color: '#B3B9C4',
};

const DISABLED_HINT_STYLE: React.CSSProperties = {
  marginLeft: 'auto',
  fontSize: 12,
  fontWeight: 400,
  color: '#9699a6',
};

// ─── Icons (SVG, ported from Jira plugin) ────────────────────────────────────

const IconScheduleItem: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const IconRequestLeave: React.FC = () => (
  // Beach umbrella — suggests vacation / time off, visually distinct from the
  // clock used for "Log time".
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M23 12a11.05 11.05 0 0 0-22 0" />
    <line x1="1" y1="12" x2="23" y2="12" />
    <path d="M12 12v7" />
    <path d="M12 19a3 3 0 0 1-3-3" />
  </svg>
);

const IconLogTime: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

// ─── Component ───────────────────────────────────────────────────────────────

interface MenuAction {
  key: string;
  label: React.ReactNode;
  icon: React.FC;
  disabled: boolean;
  disabledHint?: string;
  onSelect: () => void;
}

/**
 * Small popup menu anchored at the scheduler cell click location.
 * Visual match to the Jira plugin's CellContextMenu (same padding, radii,
 * shadow, font, and icon set).
 */
export default function CellContextMenu({
  x,
  y,
  assigneeKind,
  assigneeName,
  isSelfRow,
  canRequestLeave,
  canLogTime,
  onCreateItem,
  onRequestLeave,
  onLogTime,
  onClose,
}: CellContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  // Adjusted position after measuring the rendered menu — keeps it fully inside
  // the viewport on small screens / clicks near the bottom or right edge.
  const [pos, setPos] = useState<{ left: number; top: number }>({ left: x, top: y });

  useLayoutEffect(() => {
    const el = menuRef.current;
    if (!el) return;
    const { offsetWidth: w, offsetHeight: h } = el;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const MARGIN = 8;
    let left = x;
    let top = y;
    if (left + w + MARGIN > vw) left = Math.max(MARGIN, vw - w - MARGIN);
    if (top + h + MARGIN > vh) top = Math.max(MARGIN, vh - h - MARGIN);
    if (left !== pos.left || top !== pos.top) setPos({ left, top });
    // Re-measure when x/y change (user clicked a new cell).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [x, y]);

  const actions = useMemo<MenuAction[]>(() => {
    const list: MenuAction[] = [];

    list.push({
      key: 'create-item',
      label: 'Create item',
      icon: IconScheduleItem,
      disabled: false,
      onSelect: onCreateItem,
    });

    if (assigneeKind === 'person' && canRequestLeave) {
      list.push({
        key: 'request-leave',
        label:
          isSelfRow || !assigneeName
            ? 'Request leave'
            : `Request leave for ${assigneeName}`,
        icon: IconRequestLeave,
        disabled: false,
        onSelect: onRequestLeave,
      });
    }

    if (assigneeKind === 'person' && canLogTime) {
      list.push({
        key: 'log-time',
        label:
          isSelfRow || !assigneeName ? 'Log time' : `Log time for ${assigneeName}`,
        icon: IconLogTime,
        // Log time works independently of configured boards — the dialog's item
        // picker queries monday.com directly.
        disabled: false,
        onSelect: onLogTime,
      });
    }

    return list;
  }, [
    assigneeKind,
    canRequestLeave,
    canLogTime,
    assigneeName,
    isSelfRow,
    onRequestLeave,
    onCreateItem,
    onLogTime,
  ]);

  const hasAnyEnabled = actions.some((a) => !a.disabled);
  const firstEnabledIndex = actions.findIndex((a) => !a.disabled);

  useEffect(() => {
    if (actions.length === 0 || !hasAnyEnabled) {
      onClose();
    }
  }, [actions.length, hasAnyEnabled, onClose]);

  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      if (!menuRef.current) return;
      if (menuRef.current.contains(e.target as Node)) return;
      // Clicks on empty scheduler cells are handled by the scheduler's own
      // mouseup listener, which toggles the menu (open → close, close → open).
      // Closing here on the mousedown would race that toggle and make the menu
      // reappear. Let the scheduler decide for cell clicks.
      const target = e.target as HTMLElement | null;
      if (target?.closest('.e-work-cells')) return;
      onClose();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    const timer = window.setTimeout(() => {
      document.addEventListener('mousedown', onMouseDown);
    }, 0);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  useEffect(() => {
    if (firstEnabledIndex >= 0) itemRefs.current[firstEnabledIndex]?.focus();
  }, [firstEnabledIndex]);

  const handleSelect = (action: MenuAction) => {
    if (action.disabled) return;
    action.onSelect();
    onClose();
  };

  const focusNextEnabled = (fromIndex: number, direction: 1 | -1) => {
    if (actions.length === 0) return;
    let i = fromIndex;
    for (let step = 0; step < actions.length; step++) {
      i = (i + direction + actions.length) % actions.length;
      if (!actions[i].disabled) {
        itemRefs.current[i]?.focus();
        return;
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      focusNextEnabled(index, 1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      focusNextEnabled(index, -1);
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleSelect(actions[index]);
    }
  };

  if (actions.length === 0 || !hasAnyEnabled) return null;

  return (
    <div
      ref={menuRef}
      role="menu"
      style={{ ...MENU_STYLE, left: pos.left, top: pos.top }}
      onContextMenu={(e) => e.preventDefault()}
    >
      {actions.map((action, index) => {
        const isHover = hoverIndex === index && !action.disabled;
        const itemStyle: React.CSSProperties = {
          ...ITEM_BASE,
          ...(action.disabled ? ITEM_DISABLED : null),
          ...(isHover ? ITEM_HOVER : null),
        };
        const iconStyle: React.CSSProperties = {
          ...ICON_SLOT,
          ...(action.disabled ? ICON_SLOT_DISABLED : null),
          ...(isHover ? ICON_SLOT_HOVER : null),
        };
        const Icon = action.icon;
        return (
          <button
            key={action.key}
            type="button"
            role="menuitem"
            disabled={action.disabled}
            style={itemStyle}
            ref={(el) => {
              itemRefs.current[index] = el;
            }}
            onClick={() => handleSelect(action)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onMouseEnter={() => setHoverIndex(index)}
            onMouseLeave={() => setHoverIndex((h) => (h === index ? null : h))}
          >
            <span style={iconStyle}>
              <Icon />
            </span>
            <span>{action.label}</span>
            {action.disabledHint && (
              <span style={DISABLED_HINT_STYLE}>{action.disabledHint}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
